import React from 'react'
import {Stack, Typography, SxProps, Theme, useTheme} from '@mui/material'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/it'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {PICKER_LOCALE_TEXT, pickerFieldSx, pickerPopupSlotProps} from './pickerStyles'

interface DatePickerNewProps {
  value: string | null
  onChange: (value: string | null) => void
  label?: string
  required?: boolean
  disabled?: boolean
  error?: boolean
  helperText?: string
  fullWidth?: boolean
  // ISO strings, forwarded to the underlying MUI X DatePicker as dayjs values — used to keep a
  // paired date range consistent (e.g. an "end date" picker cannot go before its "start date").
  minDate?: string | null
  maxDate?: string | null
  sx?: SxProps<Theme>
}

export default function DatePickerNew({
  value,
  onChange,
  label,
  required,
  disabled,
  error,
  helperText,
  fullWidth,
  minDate,
  maxDate,
  sx,
}: DatePickerNewProps): JSX.Element {
  const theme = useTheme()
  // Ensures the calendar popup/dialog always renders above any container this
  // component may be nested in (e.g. a Drawer/Dialog with a boosted z-index).
  const popupZIndex = theme.zIndex.modal + 2

  return (
    <Stack spacing={0.5} sx={{width: fullWidth ? '100%' : undefined, ...sx}}>
      {label && (
        <Typography variant="caption" sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}>
          {label}{required && ' *'}
        </Typography>
      )}
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it" localeText={PICKER_LOCALE_TEXT}>
        <DatePicker
          value={value ? dayjs(value) : null}
          onChange={(val) => onChange(val ? val.toISOString() : null)}
          disabled={disabled}
          minDate={minDate ? dayjs(minDate) : undefined}
          maxDate={maxDate ? dayjs(maxDate) : undefined}
          format="DD/MM/YYYY"
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: fullWidth ?? true,
              error,
              placeholder: 'GG/MM/AAAA',
              sx: pickerFieldSx(error),
            },
            ...pickerPopupSlotProps(popupZIndex),
          }}
        />
      </LocalizationProvider>
      {helperText && (
        <Typography variant="caption" sx={{color: error ? 'error.main' : NEUTRAL_SCALE[500]}}>
          {helperText}
        </Typography>
      )}
    </Stack>
  )
}
